import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  Users, Mail, Phone, Package, Edit, Save, X, Loader2, 
  Eye, EyeOff, Key, MapPin, Calendar, DollarSign, 
  CheckCircle, Clock, Search, ChevronDown, ChevronUp, IdCard,
  Settings, FileText
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const CustomersManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showCustomerIdModal, setShowCustomerIdModal] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [credentialData, setCredentialData] = useState({
    username: '',
    password: '',
    generatePassword: true
  });
  const [resetPasswordData, setResetPasswordData] = useState({
    password: '',
    generatePassword: true
  });
  const [customerIdData, setCustomerIdData] = useState({
    customerId: ''
  });
  const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    email: '',
    phone: '',
    alternatePhone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    plan_name: '',
    plan_price: '',
    order_id: '',
    payment_status: 'pending',
    installationDate: '',
    connectionType: 'Fiber',
    ipAddress: '',
    routerModel: '',
    notes: ''
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/customers/detailed`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (data.success && data.customers) {
        setCustomers(data.customers);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to load customers');
      setLoading(false);
    }
  };

  const handleAddCustomer = async () => {
    try {
      // Validate required fields
      if (!newCustomerData.name || !newCustomerData.email || !newCustomerData.phone) {
        toast.error('Please fill in required fields (Name, Email, Phone)');
        return;
      }

      // Split name into firstName and lastName
      const nameParts = newCustomerData.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const token = localStorage.getItem('customerAdminToken');
      
      if (!token) {
        toast.error('Session expired. Please login again.');
        return;
      }

      const requestBody = {
        firstName: firstName,
        lastName: lastName,
        mobile: newCustomerData.phone,
        email: newCustomerData.email,
        companyName: newCustomerData.plan_name || 'Individual',
        gstin: '',
        billingAddress: {
          address_line1: newCustomerData.address || '',
          address_line2: '',
          city: newCustomerData.city || '',
          state: newCustomerData.state || '',
          pincode: newCustomerData.pincode || '',
          country: 'India'
        },
        shippingAddress: null,
        outstandingAmount: 0.0
      };

      console.log('Sending customer data:', requestBody);

      const response = await fetch(`${API_BASE_URL}/api/customer-admin/customers/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      console.log('Response status:', response.status);

      // Handle authentication errors
      if (response.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('customerAdminToken');
        window.location.href = '/customer-admin/login';
        return;
      }

      const data = await response.json();
      console.log('Response data:', data);

      if (response.ok && data.message) {
        toast.success(`Customer added successfully! Account ID: ${data.customer?.accountId || ''}`);
        
        // Show generated credentials
        if (data.customer?.password) {
          toast.info(`Generated Password: ${data.customer.password}`, { duration: 10000 });
        }
        
        setShowAddCustomerModal(false);
        setNewCustomerData({
          name: '',
          email: '',
          phone: '',
          alternatePhone: '',
          address: '',
          city: '',
          state: '',
          pincode: '',
          plan_name: '',
          plan_price: '',
          order_id: '',
          payment_status: 'pending',
          installationDate: '',
          connectionType: 'Fiber',
          ipAddress: '',
          routerModel: '',
          notes: ''
        });
        fetchCustomers();
      } else {
        // Show detailed error message
        let errorMsg = 'Failed to add customer';
        if (data.detail) {
          if (Array.isArray(data.detail)) {
            errorMsg = data.detail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join(', ');
          } else {
            errorMsg = data.detail;
          }
        } else if (data.message) {
          errorMsg = data.message;
        }
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Error adding customer:', error);
      toast.error(`Failed to add customer: ${error.message || 'Network error'}`);
    }
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer.orderId);
    setEditFormData({
      fullName: customer.fullName,
      email: customer.email,
      mobileNumber: customer.mobileNumber,
      address: customer.address || '',
      city: customer.city || '',
      state: customer.state || '',
      pinCode: customer.pinCode || '',
      service: customer.service,
      plan: customer.plan,
      notes: customer.notes || ''
    });
  };

  const handleSaveCustomer = async (orderId) => {
    try {
      const token = localStorage.getItem('customerAdminToken');
      const formData = new FormData();
      
      Object.keys(editFormData).forEach(key => {
        formData.append(key, editFormData[key]);
      });

      const response = await fetch(`${API_BASE_URL}/api/customer-admin/customers/${orderId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Customer details updated!');
        setEditingCustomer(null);
        fetchCustomers();
      } else {
        toast.error(data.message || 'Update failed');
      }
    } catch (error) {
      console.error('Error updating customer:', error);
      toast.error('Failed to update customer');
    }
  };

  const handleCreateCredentials = async () => {
    if (!credentialData.username) {
      toast.error('Username is required');
      return;
    }

    if (!credentialData.generatePassword && !credentialData.password) {
      toast.error('Password is required');
      return;
    }

    try {
      const token = localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/create-portal-credentials`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          orderId: selectedCustomer.orderId,
          customerEmail: selectedCustomer.email,
          username: credentialData.username,
          password: credentialData.generatePassword ? null : credentialData.password,
          generatePassword: credentialData.generatePassword
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Portal credentials created successfully!');
        if (data.generatedPassword) {
          toast.success(`Generated Password: ${data.generatedPassword}`, { duration: 10000 });
        }
        setShowCredentialsModal(false);
        setCredentialData({ username: '', password: '', generatePassword: true });
        fetchCustomers();
      } else {
        toast.error(data.message || 'Failed to create credentials');
      }
    } catch (error) {
      console.error('Error creating credentials:', error);
      toast.error('Failed to create credentials');
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordData.generatePassword && !resetPasswordData.password) {
      toast.error('Password is required');
      return;
    }

    try {
      const token = localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/reset-portal-password`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          orderId: selectedCustomer.orderId,
          password: resetPasswordData.generatePassword ? null : resetPasswordData.password,
          generatePassword: resetPasswordData.generatePassword
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Password reset successfully!');
        if (data.generatedPassword) {
          toast.success(`New Password: ${data.generatedPassword}`, { duration: 10000 });
        }
        setShowResetModal(false);
        setResetPasswordData({ password: '', generatePassword: true });
        fetchCustomers();
      } else {
        toast.error(data.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Failed to reset password');
    }
  };

  const handleAssignCustomerId = async () => {
    if (!customerIdData.customerId || customerIdData.customerId.trim() === '') {
      toast.error('Customer ID is required');
      return;
    }

    try {
      const token = localStorage.getItem('customerAdminToken');
      const formData = new FormData();
      formData.append('customerId', customerIdData.customerId.trim());

      const response = await fetch(`${API_BASE_URL}/api/customer-admin/customers/${selectedCustomer.email}/assign-customer-id`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Customer ID assigned successfully!');
        setShowCustomerIdModal(false);
        setCustomerIdData({ customerId: '' });
        setSelectedCustomer(null);
        fetchCustomers();
      } else {
        toast.error(data.message || 'Failed to assign customer ID');
      }
    } catch (error) {
      console.error('Error assigning customer ID:', error);
      toast.error('Failed to assign customer ID');
    }
  };


  const getFilteredCustomers = () => {
    if (!searchTerm) return customers;
    
    return customers.filter(c => 
      c.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.mobileNumber.includes(searchTerm) ||
      c.orderId.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Customer Management</h2>
          <p className="text-gray-600 mt-1">Manage all active customer details from billing system</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Add New Customer Button */}
          <Button
            onClick={() => setShowAddCustomerModal(true)}
            className="bg-gradient-to-r from-red-500 to-pink-600 text-white hover:from-red-600 hover:to-pink-700 font-bold px-6 py-6 rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105 flex items-center gap-2"
          >
            <Users className="h-5 w-5" />
            ADD NEW CUSTOMER
          </Button>
          
          {/* Total Customers Card */}
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 px-6 py-4 rounded-lg shadow-lg">
            <p className="text-sm text-white/90 font-semibold">Total Customers</p>
            <p className="text-3xl font-black text-white">{customers.length}</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name, email, mobile, or order ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : getFilteredCustomers().length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No customers found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {getFilteredCustomers().map((customer) => (
            <Card key={customer.orderId} className="border-2 hover:border-blue-300 transition-all">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Customer Header */}
                    <div 
                      className="flex items-center gap-3 mb-4 cursor-pointer"
                      onClick={() => setExpandedCustomer(expandedCustomer === customer.orderId ? null : customer.orderId)}
                    >
                      <div className="bg-blue-100 p-3 rounded-full">
                        <Users className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="text-xl font-bold text-gray-900">{customer.fullName}</h3>
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 inline mr-1" />
                            ACTIVE
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">Order ID: {customer.orderId}</p>
                        {(customer.customerId || customer.accountId) && (
                          <p className="text-sm text-teal-700 font-semibold flex items-center gap-1 mt-1">
                            <IdCard className="h-3 w-3" />
                            Customer ID: {customer.customerId || customer.accountId}
                          </p>
                        )}
                        <div className="flex gap-4 text-sm text-gray-600 mt-1">
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {customer.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {customer.mobileNumber}
                          </span>
                        </div>
                      </div>
                      {expandedCustomer === customer.orderId ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </div>

                    {/* Expanded Details */}
                    {expandedCustomer === customer.orderId && (
                      <div className="space-y-4 border-t pt-4">
                        {editingCustomer === customer.orderId ? (
                          // Edit Mode
                          <div className="space-y-4 bg-blue-50 p-4 rounded-lg">
                            <div className="flex justify-between items-center border-b pb-2">
                              <h4 className="font-bold text-gray-900">Edit Customer Details</h4>
                              <div className="flex gap-2">
                                <Button onClick={() => handleSaveCustomer(customer.orderId)} size="sm" className="bg-green-600 hover:bg-green-700">
                                  <Save className="h-4 w-4 mr-2" />
                                  Save
                                </Button>
                                <Button onClick={() => setEditingCustomer(null)} size="sm" variant="outline">
                                  <X className="h-4 w-4 mr-2" />
                                  Cancel
                                </Button>
                              </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <Label>Full Name</Label>
                                <Input
                                  value={editFormData.fullName}
                                  onChange={(e) => setEditFormData({...editFormData, fullName: e.target.value})}
                                />
                              </div>
                              <div>
                                <Label>Email</Label>
                                <Input
                                  type="email"
                                  value={editFormData.email}
                                  onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                                />
                              </div>
                              <div>
                                <Label>Mobile Number</Label>
                                <Input
                                  value={editFormData.mobileNumber}
                                  onChange={(e) => setEditFormData({...editFormData, mobileNumber: e.target.value})}
                                />
                              </div>
                              <div>
                                <Label>Service</Label>
                                <Input
                                  value={editFormData.service}
                                  onChange={(e) => setEditFormData({...editFormData, service: e.target.value})}
                                />
                              </div>
                              <div>
                                <Label>Plan</Label>
                                <Input
                                  value={editFormData.plan}
                                  onChange={(e) => setEditFormData({...editFormData, plan: e.target.value})}
                                />
                              </div>
                              <div>
                                <Label>Address</Label>
                                <Input
                                  value={editFormData.address}
                                  onChange={(e) => setEditFormData({...editFormData, address: e.target.value})}
                                />
                              </div>
                              <div>
                                <Label>City</Label>
                                <Input
                                  value={editFormData.city}
                                  onChange={(e) => setEditFormData({...editFormData, city: e.target.value})}
                                />
                              </div>
                              <div>
                                <Label>State</Label>
                                <Input
                                  value={editFormData.state}
                                  onChange={(e) => setEditFormData({...editFormData, state: e.target.value})}
                                />
                              </div>
                              <div>
                                <Label>PIN Code</Label>
                                <Input
                                  value={editFormData.pinCode}
                                  onChange={(e) => setEditFormData({...editFormData, pinCode: e.target.value})}
                                />
                              </div>
                              <div className="md:col-span-2">
                                <Label>Notes / Updates</Label>
                                <Input
                                  value={editFormData.notes}
                                  onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                                  placeholder="Add notes or updates..."
                                />
                              </div>
                            </div>
                          </div>
                        ) : (
                          // View Mode
                          <div className="space-y-4">
                            {/* Contact & Location */}
                            <div className="grid md:grid-cols-3 gap-3">
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <p className="text-xs text-gray-600 mb-1">Service & Plan</p>
                                <p className="font-semibold text-blue-700">{customer.service}</p>
                                <p className="text-sm text-gray-600">{customer.plan}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <p className="text-xs text-gray-600 mb-1">Location</p>
                                <p className="font-semibold text-gray-900">{customer.city || 'N/A'}, {customer.state || 'N/A'}</p>
                                <p className="text-sm text-gray-600">PIN: {customer.pinCode || 'N/A'}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <p className="text-xs text-gray-600 mb-1">Activation Date</p>
                                <p className="font-semibold text-gray-900">{formatDate(customer.activationDate)}</p>
                              </div>
                            </div>

                            {/* Financial Summary */}
                            <div className="grid md:grid-cols-4 gap-3">
                              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                                <p className="text-xs text-blue-700 font-semibold">Total Billed</p>
                                <p className="text-lg font-bold text-blue-900">{formatCurrency(customer.totalInvoiceAmount)}</p>
                              </div>
                              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                                <p className="text-xs text-green-700 font-semibold">Collection</p>
                                <p className="text-lg font-bold text-green-900">{formatCurrency(customer.totalCollection)}</p>
                              </div>
                              <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                                <p className="text-xs text-red-700 font-semibold">Dues</p>
                                <p className="text-lg font-bold text-red-900">{formatCurrency(customer.currentDues)}</p>
                              </div>
                              <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                                <p className="text-xs text-yellow-700 font-semibold">Pending Invoices</p>
                                <p className="text-lg font-bold text-yellow-900">{customer.pendingInvoices || 0}</p>
                              </div>
                            </div>

                            {/* Device Details if available */}
                            {customer.deviceDetails && Object.keys(customer.deviceDetails).length > 0 && (
                              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                                <h5 className="font-semibold text-purple-900 mb-2">Device & Technical Details</h5>
                                <div className="grid md:grid-cols-3 gap-2 text-sm">
                                  {customer.deviceDetails.routerModel && (
                                    <div><span className="text-gray-600">Router:</span> <span className="font-semibold">{customer.deviceDetails.routerModel}</span></div>
                                  )}
                                  {customer.deviceDetails.circuitId && (
                                    <div><span className="text-gray-600">Circuit ID:</span> <span className="font-semibold">{customer.deviceDetails.circuitId}</span></div>
                                  )}
                                  {customer.deviceDetails.ispName && (
                                    <div><span className="text-gray-600">ISP:</span> <span className="font-semibold">{customer.deviceDetails.ispName}</span></div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Portal Credentials Status */}
                            {customer.portalCredentials ? (
                              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                                <p className="text-sm text-green-800">
                                  <Key className="h-4 w-4 inline mr-2" />
                                  Portal Access: <span className="font-bold">Active</span> | Username: <span className="font-mono">{customer.portalCredentials.username}</span>
                                </p>
                              </div>
                            ) : (
                              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                                <p className="text-sm text-gray-600">
                                  <Key className="h-4 w-4 inline mr-2" />
                                  No portal access credentials created yet
                                </p>
                              </div>
                            )}

                            {/* Notes */}
                            {customer.notes && (
                              <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                                <p className="text-xs text-yellow-700 font-semibold mb-1">Notes / Updates</p>
                                <p className="text-sm text-gray-800">{customer.notes}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-2 ml-4">
                    <Button
                      onClick={() => handleEditCustomer(customer)}
                      className="bg-blue-600 hover:bg-blue-700 gap-2"
                      size="sm"
                    >
                      <Edit className="h-4 w-4" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => {
                        setSelectedCustomer(customer);
                        setCustomerIdData({
                          customerId: customer.customerId || customer.accountId || ''
                        });
                        setShowCustomerIdModal(true);
                      }}
                      className="bg-teal-600 hover:bg-teal-700 gap-2"
                      size="sm"
                    >
                      <IdCard className="h-4 w-4" />
                      Set Customer ID
                    </Button>
                    {!customer.portalCredentials ? (
                      <Button
                        onClick={() => {
                          setSelectedCustomer(customer);
                          setCredentialData({
                            username: customer.email.split('@')[0],
                            password: '',
                            generatePassword: true
                          });
                          setShowCredentialsModal(true);
                        }}
                        className="bg-purple-600 hover:bg-purple-700 gap-2"
                        size="sm"
                      >
                        <Key className="h-4 w-4" />
                        Create Login
                      </Button>
                    ) : (
                      <Button
                        onClick={() => {
                          setSelectedCustomer(customer);
                          setResetPasswordData({
                            password: '',
                            generatePassword: true
                          });
                          setShowResetModal(true);
                        }}
                        className="bg-orange-600 hover:bg-orange-700 gap-2"
                        size="sm"
                      >
                        <Key className="h-4 w-4" />
                        Reset Password
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Credentials Modal */}
      {showCredentialsModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6 space-y-4">
              <div className="flex justify-between items-center border-b pb-3">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Create Customer Portal Login</h3>
                  <p className="text-sm text-gray-600">{selectedCustomer.fullName}</p>
                </div>
                <button
                  onClick={() => {
                    setShowCredentialsModal(false);
                    setSelectedCustomer(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-3">
                <div>
                  <Label>Username *</Label>
                  <Input
                    value={credentialData.username}
                    onChange={(e) => setCredentialData({...credentialData, username: e.target.value})}
                    placeholder="customer_username"
                  />
                </div>

                <div className="flex items-center gap-2 bg-blue-50 p-3 rounded">
                  <input
                    type="checkbox"
                    checked={credentialData.generatePassword}
                    onChange={(e) => setCredentialData({...credentialData, generatePassword: e.target.checked})}
                    className="h-4 w-4"
                  />
                  <Label className="cursor-pointer">Auto-generate secure password</Label>
                </div>

                {!credentialData.generatePassword && (
                  <div>
                    <Label>Password *</Label>
                    <Input
                      type="password"
                      value={credentialData.password}
                      onChange={(e) => setCredentialData({...credentialData, password: e.target.value})}
                      placeholder="Enter password"
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleCreateCredentials}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  <Key className="h-4 w-4 mr-2" />
                  Create Credentials
                </Button>
                <Button
                  onClick={() => {
                    setShowCredentialsModal(false);
                    setSelectedCustomer(null);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6 space-y-4">
              <div className="flex justify-between items-center border-b pb-3">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Reset Customer Password</h3>
                  <p className="text-sm text-gray-600">{selectedCustomer.fullName}</p>
                  <p className="text-xs text-gray-500 mt-1">Username: <span className="font-mono">{selectedCustomer.portalCredentials?.username}</span></p>
                </div>
                <button
                  onClick={() => {
                    setShowResetModal(false);
                    setSelectedCustomer(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
                <p className="text-xs text-yellow-800">
                  ⚠️ <strong>Warning:</strong> This will replace the existing password. Make sure to share the new password with the customer.
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2 bg-blue-50 p-3 rounded">
                  <input
                    type="checkbox"
                    checked={resetPasswordData.generatePassword}
                    onChange={(e) => setResetPasswordData({...resetPasswordData, generatePassword: e.target.checked})}
                    className="h-4 w-4"
                  />
                  <Label className="cursor-pointer">Auto-generate secure password</Label>
                </div>

                {!resetPasswordData.generatePassword && (
                  <div>
                    <Label>New Password *</Label>
                    <Input
                      type="password"
                      value={resetPasswordData.password}
                      onChange={(e) => setResetPasswordData({...resetPasswordData, password: e.target.value})}
                      placeholder="Enter new password"
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleResetPassword}
                  className="flex-1 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700"
                >
                  <Key className="h-4 w-4 mr-2" />
                  Reset Password
                </Button>
                <Button
                  onClick={() => {
                    setShowResetModal(false);
                    setSelectedCustomer(null);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Enhanced Add New Customer Modal */}
      {showAddCustomerModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-5xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Header with Gradient */}
            <div className="bg-gradient-to-r from-red-500 via-pink-500 to-purple-600 p-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 backdrop-blur-md rounded-full p-3">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-3xl font-black text-white">Add New Customer</h3>
                    <p className="text-white/90 text-sm mt-1">Fill in the details to create a new customer account</p>
                  </div>
                </div>
                <Button
                  onClick={() => setShowAddCustomerModal(false)}
                  className="bg-white/20 hover:bg-white/30 text-white border-0 rounded-full"
                  size="icon"
                >
                  <X className="h-6 w-6" />
                </Button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-6">
              <div className="space-y-6">
                {/* Basic Information - Enhanced */}
                <div className="relative overflow-hidden rounded-xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 p-5 shadow-md">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-blue-200 rounded-full blur-3xl opacity-30"></div>
                  <h4 className="font-black text-blue-900 mb-4 flex items-center gap-2 text-lg relative z-10">
                    <div className="bg-blue-500 rounded-lg p-2">
                      <Users className="h-5 w-5 text-white" />
                    </div>
                    Personal Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 relative z-10">
                    <div>
                      <Label htmlFor="name" className="text-blue-900 font-semibold flex items-center gap-1">
                        Full Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="name"
                        value={newCustomerData.name}
                        onChange={(e) => setNewCustomerData({...newCustomerData, name: e.target.value})}
                        placeholder="Enter full name"
                        className="border-blue-300 focus:border-blue-500 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-blue-900 font-semibold flex items-center gap-1">
                        Email Address <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={newCustomerData.email}
                        onChange={(e) => setNewCustomerData({...newCustomerData, email: e.target.value})}
                        placeholder="email@example.com"
                        className="border-blue-300 focus:border-blue-500 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-blue-900 font-semibold flex items-center gap-1">
                        Primary Phone <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="phone"
                        value={newCustomerData.phone}
                        onChange={(e) => setNewCustomerData({...newCustomerData, phone: e.target.value})}
                        placeholder="+91 XXXXX XXXXX"
                        className="border-blue-300 focus:border-blue-500 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="alternatePhone" className="text-blue-900 font-semibold">
                        Alternate Phone
                      </Label>
                      <Input
                        id="alternatePhone"
                        value={newCustomerData.alternatePhone}
                        onChange={(e) => setNewCustomerData({...newCustomerData, alternatePhone: e.target.value})}
                        placeholder="+91 XXXXX XXXXX"
                        className="border-blue-300 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Address Information - Enhanced */}
                <div className="relative overflow-hidden rounded-xl border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 p-5 shadow-md">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-green-200 rounded-full blur-3xl opacity-30"></div>
                  <h4 className="font-black text-green-900 mb-4 flex items-center gap-2 text-lg relative z-10">
                    <div className="bg-green-500 rounded-lg p-2">
                      <MapPin className="h-5 w-5 text-white" />
                    </div>
                    Address Details
                  </h4>
                  <div className="grid grid-cols-1 gap-4 relative z-10">
                    <div>
                      <Label htmlFor="address" className="text-green-900 font-semibold">
                        Installation Address
                      </Label>
                      <Input
                        id="address"
                        value={newCustomerData.address}
                        onChange={(e) => setNewCustomerData({...newCustomerData, address: e.target.value})}
                        placeholder="Building, Street, Locality"
                        className="border-green-300 focus:border-green-500 focus:ring-green-500"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="city" className="text-green-900 font-semibold">City</Label>
                        <Input
                          id="city"
                          value={newCustomerData.city}
                          onChange={(e) => setNewCustomerData({...newCustomerData, city: e.target.value})}
                          placeholder="City name"
                          className="border-green-300 focus:border-green-500 focus:ring-green-500"
                        />
                      </div>
                      <div>
                        <Label htmlFor="state" className="text-green-900 font-semibold">State</Label>
                        <Input
                          id="state"
                          value={newCustomerData.state}
                          onChange={(e) => setNewCustomerData({...newCustomerData, state: e.target.value})}
                          placeholder="State name"
                          className="border-green-300 focus:border-green-500 focus:ring-green-500"
                        />
                      </div>
                      <div>
                        <Label htmlFor="pincode" className="text-green-900 font-semibold">PIN Code</Label>
                        <Input
                          id="pincode"
                          value={newCustomerData.pincode}
                          onChange={(e) => setNewCustomerData({...newCustomerData, pincode: e.target.value})}
                          placeholder="6-digit PIN"
                          className="border-green-300 focus:border-green-500 focus:ring-green-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Service & Plan Details - Enhanced */}
                <div className="relative overflow-hidden rounded-xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 p-5 shadow-md">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-purple-200 rounded-full blur-3xl opacity-30"></div>
                  <h4 className="font-black text-purple-900 mb-4 flex items-center gap-2 text-lg relative z-10">
                    <div className="bg-purple-500 rounded-lg p-2">
                      <Package className="h-5 w-5 text-white" />
                    </div>
                    Service & Plan Configuration
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 relative z-10">
                    <div>
                      <Label htmlFor="planName" className="text-purple-900 font-semibold">Service/Plan Name</Label>
                      <Input
                        id="planName"
                        value={newCustomerData.plan_name}
                        onChange={(e) => setNewCustomerData({...newCustomerData, plan_name: e.target.value})}
                        placeholder="e.g., Lease Line, Fiber 100Mbps"
                        className="border-purple-300 focus:border-purple-500 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="planPrice" className="text-purple-900 font-semibold">Monthly Price (₹)</Label>
                      <Input
                        id="planPrice"
                        type="number"
                        value={newCustomerData.plan_price}
                        onChange={(e) => setNewCustomerData({...newCustomerData, plan_price: e.target.value})}
                        placeholder="Enter amount"
                        className="border-purple-300 focus:border-purple-500 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="connectionType" className="text-purple-900 font-semibold">Connection Type</Label>
                      <select
                        id="connectionType"
                        value={newCustomerData.connectionType}
                        onChange={(e) => setNewCustomerData({...newCustomerData, connectionType: e.target.value})}
                        className="w-full px-3 py-2 border-2 border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="Fiber">Fiber Optic</option>
                        <option value="DSL">DSL</option>
                        <option value="Cable">Cable</option>
                        <option value="Wireless">Wireless</option>
                        <option value="Lease Line">Lease Line</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="installationDate" className="text-purple-900 font-semibold">Installation Date</Label>
                      <Input
                        id="installationDate"
                        type="date"
                        value={newCustomerData.installationDate}
                        onChange={(e) => setNewCustomerData({...newCustomerData, installationDate: e.target.value})}
                        className="border-purple-300 focus:border-purple-500 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="orderId" className="text-purple-900 font-semibold">Order ID</Label>
                      <Input
                        id="orderId"
                        value={newCustomerData.order_id}
                        onChange={(e) => setNewCustomerData({...newCustomerData, order_id: e.target.value})}
                        placeholder="Auto-generated if empty"
                        className="border-purple-300 focus:border-purple-500 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="paymentStatus" className="text-purple-900 font-semibold">Payment Status</Label>
                      <select
                        id="paymentStatus"
                        value={newCustomerData.payment_status}
                        onChange={(e) => setNewCustomerData({...newCustomerData, payment_status: e.target.value})}
                        className="w-full px-3 py-2 border-2 border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="pending">🟡 Pending</option>
                        <option value="paid">✅ Paid</option>
                        <option value="partial">🟠 Partial</option>
                        <option value="overdue">🔴 Overdue</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Technical Details - New Section */}
                <div className="relative overflow-hidden rounded-xl border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50 p-5 shadow-md">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-orange-200 rounded-full blur-3xl opacity-30"></div>
                  <h4 className="font-black text-orange-900 mb-4 flex items-center gap-2 text-lg relative z-10">
                    <div className="bg-orange-500 rounded-lg p-2">
                      <Settings className="h-5 w-5 text-white" />
                    </div>
                    Technical Configuration
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 relative z-10">
                    <div>
                      <Label htmlFor="ipAddress" className="text-orange-900 font-semibold">Static IP Address</Label>
                      <Input
                        id="ipAddress"
                        value={newCustomerData.ipAddress}
                        onChange={(e) => setNewCustomerData({...newCustomerData, ipAddress: e.target.value})}
                        placeholder="e.g., 192.168.1.100"
                        className="border-orange-300 focus:border-orange-500 focus:ring-orange-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="routerModel" className="text-orange-900 font-semibold">Router Model</Label>
                      <Input
                        id="routerModel"
                        value={newCustomerData.routerModel}
                        onChange={(e) => setNewCustomerData({...newCustomerData, routerModel: e.target.value})}
                        placeholder="e.g., TP-Link Archer C6"
                        className="border-orange-300 focus:border-orange-500 focus:ring-orange-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Notes - New Section */}
                <div className="relative overflow-hidden rounded-xl border-2 border-gray-200 bg-gradient-to-br from-gray-50 to-slate-50 p-5 shadow-md">
                  <h4 className="font-black text-gray-900 mb-4 flex items-center gap-2 text-lg">
                    <div className="bg-gray-500 rounded-lg p-2">
                      <FileText className="h-5 w-5 text-white" />
                    </div>
                    Additional Notes
                  </h4>
                  <div>
                    <Label htmlFor="notes" className="text-gray-900 font-semibold">Internal Notes/Comments</Label>
                    <textarea
                      id="notes"
                      value={newCustomerData.notes}
                      onChange={(e) => setNewCustomerData({...newCustomerData, notes: e.target.value})}
                      placeholder="Add any special instructions, requirements, or notes about this customer..."
                      rows={3}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                    />
                  </div>
                </div>

              </div>
            </div>

            {/* Fixed Footer with Action Buttons */}
            <div className="sticky bottom-0 bg-gradient-to-r from-gray-50 to-slate-50 border-t-2 border-gray-200 p-6">
              <div className="flex gap-4">
                <Button
                  onClick={handleAddCustomer}
                  className="flex-1 bg-gradient-to-r from-red-500 via-pink-500 to-purple-600 text-white hover:from-red-600 hover:via-pink-600 hover:to-purple-700 font-bold py-6 text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                >
                  <Users className="h-5 w-5 mr-2" />
                  Create Customer Account
                </Button>
                <Button
                  onClick={() => {
                    setShowAddCustomerModal(false);
                    setNewCustomerData({
                      name: '',
                      email: '',
                      phone: '',
                      alternatePhone: '',
                      address: '',
                      city: '',
                      state: '',
                      pincode: '',
                      plan_name: '',
                      plan_price: '',
                      order_id: '',
                      payment_status: 'pending',
                      installationDate: '',
                      connectionType: 'Fiber',
                      ipAddress: '',
                      routerModel: '',
                      notes: ''
                    });
                  }}
                  variant="outline"
                  className="flex-1 border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-100 font-bold py-6 text-lg"
                >
                  <X className="h-5 w-5 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Set Customer ID Modal */}
      {showCustomerIdModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6 space-y-4">
              <div className="flex justify-between items-center border-b pb-3">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Set Customer ID</h3>
                  <p className="text-sm text-gray-600">{selectedCustomer.fullName}</p>
                  <p className="text-xs text-gray-500 mt-1">Email: {selectedCustomer.email}</p>
                </div>
                <button
                  onClick={() => {
                    setShowCustomerIdModal(false);
                    setSelectedCustomer(null);
                    setCustomerIdData({ customerId: '' });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                <p className="text-xs text-blue-800">
                  💡 <strong>Info:</strong> This Customer ID will be visible on the customer's dashboard and can be used for identification purposes.
                </p>
              </div>

              <div className="space-y-3">
                <div>
                  <Label>Customer ID *</Label>
                  <Input
                    value={customerIdData.customerId}
                    onChange={(e) => setCustomerIdData({customerId: e.target.value})}
                    placeholder="e.g., NF/MH/SO/00001IN"
                    className="font-mono"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Current ID: {selectedCustomer.customerId || selectedCustomer.accountId || 'Not set'}
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleAssignCustomerId}
                  className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
                >
                  <IdCard className="h-4 w-4 mr-2" />
                  Assign Customer ID
                </Button>
                <Button
                  onClick={() => {
                    setShowCustomerIdModal(false);
                    setSelectedCustomer(null);
                    setCustomerIdData({ customerId: '' });
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CustomersManagement;
